import { ApolloError, gql, useQuery } from '@apollo/client';
import { Event, MarketWithAll } from 'modules/events/basics/types/events.types';
import { eventFragment } from 'modules/events/graphql/fragments/event.fragments';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const GET_FULL_PRIVATE_EVENTS = gql`
  query getFullPrivateEvents($source: Source, $filters: EventsFilterInput, $limit: Int, $offset: Int, $sortOrder: String) {
    getFullPrivateEvents(source: $source, filters: $filters, limit: $limit, offset: $offset, sortOrder: $sortOrder) {
      count
      events {
      ${eventFragment}
      data {
        ...on OnlineEvent {
          __typename
          session {
            startDate
            endDate
          }
          provider {
            host
            participantUrl
            organizerUrl
            brightcoveId
            licenseId
            providedId
          }
          biogenLincConfig {
            showSendInvitations
            slug
            isVisibleOnBiogenLinc
            isSwitzerlandSpecific
          }
          invitationSent
          invitationEmailEnabled
          qrCodeRegistration
          signatureCollection
        },
        ...on PhysicalEvent {
          __typename
          session {
            startDate
            endDate
          }
          biogenLincConfig {
            showSendInvitations
            slug
            isVisibleOnBiogenLinc
            isSwitzerlandSpecific
          }
          invitationSent
          invitationEmailEnabled
          qrCodeRegistration
          signatureCollection
        },
        ...on HybridEvent {
          __typename
          session {
            startDate
            endDate
          }
          provider {
            host
            participantUrl
            organizerUrl
            brightcoveId
            licenseId
            providedId
          }
          biogenLincConfig {
            showSendInvitations
            slug
            isVisibleOnBiogenLinc
            isSwitzerlandSpecific
          }
          invitationSent
          invitationEmailEnabled
          qrCodeRegistration
          signatureCollection
        },
      }
      }
    }
  }
`;

type ResultType = {
  getFullPrivateEvents: {
    events: Event[];
    count: number;
  };
};

type UseGetEvents = (
  market: MarketWithAll | null,
  isPast: boolean,
  showRsvpEvents: boolean,
  limit?: number,
  offset?: number,
  sortOrder?: string
) => {
  loading: boolean;
  error?: ApolloError;
  data?: Event[];
  count: number;
};

const useGetFullPrivateEventsQuery: UseGetEvents = (market, isPast, showRsvpEvents, limit, offset, sortOrder) => {
  const { data, loading, error } = useQuery<ResultType>(GET_FULL_PRIVATE_EVENTS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    variables: {
      source: 'backoffice',
      filters: {
        market,
        isPast,
        isRsvpType: showRsvpEvents,
      },
      limit,
      offset,
      sortOrder,
    },
  });

  if (error) {
    const customContext = extractApolloErrorDetails(error);
    Logger.logError({
      error,
      customContext,
      transactionName: 'getEvents.ts - useGetFullPrivateEventsQuery',
    });
  }

  return {
    data: data?.getFullPrivateEvents?.events || [],
    count: data?.getFullPrivateEvents?.count || 0,
    loading,
    error,
  };
};

export default useGetFullPrivateEventsQuery;
